import { useState } from "react";
import styled from "@emotion/styled";
import { toast } from "sonner";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useDownloadCustomerDocuments } from "lib/hooks/api/customer_documents/useDownloadCustomerDocument";
import { DeleteDocumentType } from "lib/types/graphQLEnums";
import { DeleteCustomerDocumentModal } from "../../content_library/modals/DeleteCustomerDocumentModal";

type Props = {
  id: string;
  selectedDocuments: CustomerDocumentDetails[];
  onClearSelection: () => void;
};

export default function BidDocumentsActionBar({ id, selectedDocuments, onClearSelection }: Props) {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: downloadAllDocuments } = useDownloadCustomerDocuments({
    onError: () => toast.error(`Failed to download all documents`),
  });

  return (
    <>
      <Container aria-label="Bid documents bulk action bar">
        <Flex gap={12} alignItems="center">
          <Text color="white">{`${selectedDocuments.length} selected:`}</Text>
          <ActionButtonWrapper>
            <Button
              aria-label="Delete selected documents"
              icon="trash01"
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </ActionButtonWrapper>
          <ActionButtonWrapper>
            <Button
              aria-label="Download selected documents"
              icon="download01"
              onClick={() =>
                downloadAllDocuments({
                  documents: selectedDocuments,
                  zipFileName: `stotles_bid_documents_${id}.zip`,
                })
              }
            >
              Download
            </Button>
          </ActionButtonWrapper>
        </Flex>
        <Flex gap={8} alignItems="center">
          <Button aria-label="Close" icon="xClose" onClick={onClearSelection} />
        </Flex>
      </Container>
      <DeleteCustomerDocumentModal
        selectedDocuments={selectedDocuments}
        isOpen={isDeleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={() => {
          onClearSelection();
          setDeleteModalOpen(false);
        }}
        documentType={DeleteDocumentType.BidDocument}
      />
    </>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  width: "100%",
  alignItems: "center",
  height: 48,
  padding: 16,
  backgroundColor: theme.colors.sysPrimaryDefault,
  borderRadius: 4,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
}));

const ActionButtonWrapper = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysTextWhite}`,
  borderRadius: 4,
  position: "relative",
}));
